<template>
  <div class="crash-game">
    <!-- Topbar -->
    <div class="game-topbar">
      <button class="btn-back" @click="router.push({ name: 'player.lobby' })">{{ $t('games.back_to_casino') }}</button>
      <span class="game-title-bar">
        <AppIcon name="game-crash" :size="18" /> {{ $t('games.crash.title') }}
        <span class="game-tag">{{ $t('games.crash.tag') }}</span>
      </span>
      <div class="game-topbar-actions">
        <button class="icon-btn" :title="$t('games.provably_fair')" @click="router.push({ name: 'player.provably-fair' })">
          <AppIcon name="provably-fair" :size="16" />
        </button>
      </div>
    </div>

    <!-- Game Body -->
    <div class="game-body">
      <!-- Canvas area: contains the multiplier graph -->
      <div class="game-canvas-area" ref="canvasWrap">
        <div class="canvas-bg-grid"></div>

        <!-- Graph canvas (fills entire canvas area) -->
        <canvas ref="chartCanvas" class="crash-chart"></canvas>

        <!-- Betting countdown overlay -->
        <Transition name="overlay-fade">
          <div v-if="phase === 'betting'" class="phase-overlay betting-overlay">
            <div class="overlay-label">{{ $t('games.crash.round_starting') }}</div>
            <div class="overlay-countdown">{{ bettingCountdown }}s</div>
            <div class="overlay-hint">{{ $t('games.crash.place_bets') }}</div>
          </div>
        </Transition>

        <!-- Idle overlay -->
        <Transition name="overlay-fade">
          <div v-if="phase === 'idle'" class="phase-overlay idle-overlay">
            <div class="overlay-icon"><AppIcon name="game-crash" :size="32" /></div>
            <div class="overlay-label">{{ $t('games.crash.waiting_next_round') }}</div>
          </div>
        </Transition>

        <!-- Live multiplier display (center, during running) -->
        <Transition name="mult-pop">
          <div v-if="phase === 'running'" class="live-mult-display">
            <div class="live-mult-value" :class="multColorClass">
              {{ currentMultiplier.toFixed(2) }}<span class="mult-x">×</span>
            </div>
          </div>
        </Transition>

        <!-- Crash result display -->
        <Transition name="mult-pop">
          <div v-if="phase === 'crashed'" class="crash-display">
            <div class="crash-value">{{ crashPoint?.toFixed(2) }}<span class="mult-x">×</span></div>
            <div class="crash-label">{{ $t('games.crash.crashed_label') }}</div>
            <template v-if="myBet">
              <div v-if="myBet.status === 'cashed_out'" class="player-result win">
                {{ $t('games.crash.you_won', { amount: myBet.win_amount.toFixed(2), multiplier: myBet.cashout_multiplier?.toFixed(2) }) }}
              </div>
              <div v-else-if="myBet.status === 'lost'" class="player-result loss"><AppIcon name="bust" :size="14" /> {{ $t('games.crash.busted') }}</div>
            </template>
            <button
              v-if="currentCrashedEntry"
              class="pf-verify-link"
              @click="openRoundDetail(currentCrashedEntry)"
            >
              <AppIcon name="provably-fair" :size="14" /> {{ $t('games.crash.verify_provably_fair') }}
            </button>
          </div>
        </Transition>

        <!-- Round detail modal (provably fair) -->
        <Transition name="modal-fade">
          <div v-if="selectedHistoryEntry" class="round-detail-backdrop" @click.self="closeRoundDetail">
            <div class="round-detail-modal">
              <div class="rdm-header">
                <span class="rdm-title">Round #{{ selectedHistoryEntry.id }}</span>
                <button class="rdm-close" @click="closeRoundDetail">✕</button>
              </div>
              <div class="rdm-body">
                <div class="rdm-row">
                  <span class="rdm-label">{{ $t('games.crash.crashed_at') }}</span>
                  <span class="rdm-value crash-color">{{ selectedHistoryEntry.crash_point.toFixed(2) }}×</span>
                </div>
                <div v-if="selectedHistoryEntry.ended_at" class="rdm-row">
                  <span class="rdm-label">{{ $t('games.crash.ended_at') }}</span>
                  <span class="rdm-value">{{ formatDateTime(selectedHistoryEntry.ended_at) }}</span>
                </div>
                <div class="rdm-section-title">{{ $t('games.crash.verification_title') }}</div>
                <div class="rdm-row">
                  <span class="rdm-label">{{ $t('games.crash.server_seed_hash') }}</span>
                  <span class="rdm-hash">{{ selectedHistoryEntry.server_seed_hash ?? '—' }}</span>
                </div>
                <div class="rdm-row">
                  <span class="rdm-label">{{ $t('games.crash.server_seed') }}</span>
                  <span class="rdm-hash">{{ selectedHistoryEntry.server_seed ?? '—' }}</span>
                </div>
                <div class="rdm-row">
                  <span class="rdm-label">{{ $t('games.crash.outcome_hash') }}</span>
                  <span class="rdm-hash">{{ selectedHistoryEntry.outcome_hash ?? '—' }}</span>
                </div>
                <div class="rdm-explain">
                  Verify the crash point was predetermined: compute
                  <code>HMAC-SHA256(server_seed, "0")</code> and extract the first 13 hex characters
                  as a 52-bit integer <em>h</em>. Then apply
                  <code>floor((100e - h) / (e - h)) / 100</code>
                  where <em>e = 2<sup>52</sup></em>. The result must match the crash point above.
                </div>
              </div>
            </div>
          </div>
        </Transition>

        <!-- History strip (top of canvas) -->
        <div class="history-strip">
          <div
            v-for="entry in recentHistory"
            :key="entry.id"
            class="history-pill"
            :class="historyPillClass(entry.crash_point)"
            :title="`Round #${entry.id}: ${entry.crash_point.toFixed(2)}× — click for details`"
            @click="openRoundDetail(entry)"
          >
            {{ entry.crash_point.toFixed(2) }}×
          </div>
        </div>

        <!-- Legend -->
        <div class="graph-legend">
          <span class="legend-item">
            <span class="legend-dot green"></span>
            {{ $t('games.multiplier') }}
          </span>
          <span v-if="phase === 'crashed'" class="legend-item">
            <span class="legend-dot red"></span>
            {{ $t('games.crash.crashed') }}
          </span>
          <span v-if="phase === 'running'" class="legend-item elapsed">
            {{ (serverElapsedMs / 1000).toFixed(1) }}s
          </span>
        </div>
      </div>

      <!-- Sidebar -->
      <div class="game-sidebar">
        <div class="game-tabs">
          <button class="game-tab active">{{ $t('games.bet_tab') }}</button>
          <button class="game-tab" @click="router.push({ name: 'player.provably-fair' })">{{ $t('games.fair_tab') }}</button>
        </div>

        <div class="game-tab-content">
          <!-- Phase status chip -->
          <div class="phase-chip" :class="phase">
            <span class="phase-dot"></span>
            <span class="phase-text">{{ phaseLabel }}</span>
          </div>

          <!-- Bet Amount -->
          <label class="ctrl-label" style="margin-top: 14px;">{{ $t('games.bet_amount') }}</label>
          <div class="bet-input-wrap">
            <span class="bet-currency">$</span>
            <input
              class="bet-input"
              type="number"
              v-model.number="betAmount"
              min="0.01"
              step="0.01"
              :disabled="!canBet"
            />
          </div>
          <div class="bet-shortcuts">
            <button class="bet-short" :disabled="!canBet" @click="halveBet">½</button>
            <button class="bet-short" :disabled="!canBet" @click="doubleBet">2×</button>
            <button class="bet-short" :disabled="!canBet" @click="maxBet">{{ $t('games.max_label') }}</button>
          </div>

          <!-- Auto Cashout -->
          <label class="ctrl-label">{{ $t('games.crash.auto_cashout_label') }}</label>
          <input
            class="bet-input"
            type="number"
            v-model.number="autoCashoutInput"
            min="1.01"
            step="0.1"
            placeholder="Optional e.g. 2.00"
            :disabled="!canBet"
            style="margin-bottom: 14px;"
          />

          <!-- Place Bet (betting phase, not yet bet) -->
          <button
            v-if="phase === 'betting' && !hasBetThisRound"
            class="btn-action"
            :disabled="loading"
            @click="placeBet"
          >
            {{ loading ? $t('games.crash.placing') : $t('games.crash.place_bet_btn') }}
          </button>

          <!-- Bet placed, waiting for round to start -->
          <button
            v-else-if="phase === 'betting' && hasBetThisRound"
            class="btn-action bet-confirmed"
            disabled
          >
            ✓ BET PLACED — WAITING
          </button>

          <!-- Next round notice (running, no bet) -->
          <div
            v-else-if="(phase === 'running' || phase === 'crashed') && !myBet"
            class="next-round-notice"
          >
            Wait for betting phase to place a bet
          </div>

          <!-- Cashout button (running, active pending bet) -->
          <button
            v-if="phase === 'running' && myBet && myBet.status === 'pending'"
            class="btn-cashout"
            :disabled="loading"
            @click="cashout"
          >
            {{ loading ? $t('games.crash.cashing_out') : `CASH OUT @ ${currentMultiplier.toFixed(2)}×` }}
          </button>

          <!-- Cashed out indicator -->
          <div
            v-if="phase === 'running' && myBet && myBet.status === 'cashed_out'"
            class="cashed-out-badge"
          >
            ✓ {{ $t('games.crash.cashed_out_at') }} {{ myBet.cashout_multiplier?.toFixed(2) }}×
          </div>

          <!-- Auto-cashout indicator -->
          <div
            v-if="myBet && myBet.status === 'pending' && myBet.auto_cashout"
            class="auto-cashout-badge"
          >
            ⚡ {{ $t('games.crash.auto_cashout_at') }} {{ myBet.auto_cashout.toFixed(2) }}×
          </div>

          <!-- Payout preview -->
          <div
            v-if="phase === 'running' && myBet && myBet.status === 'pending'"
            class="payout-preview"
          >
            <span class="payout-label">Current payout</span>
            <span class="payout-value">${{ currentPayoutStr }}</span>
          </div>

          <!-- Stat pills during running -->
          <div v-if="phase === 'running' && myBet && myBet.status === 'pending'" class="stat-pills">
            <div class="stat-pill">
              <div class="sp-label">{{ $t('games.multiplier') }}</div>
              <div class="sp-value gold">{{ currentMultiplier.toFixed(2) }}×</div>
            </div>
            <div class="stat-pill">
              <div class="sp-label">Profit</div>
              <div class="sp-value green">${{ profitStr }}</div>
            </div>
          </div>

          <!-- Live bet list -->
          <div v-if="liveBets.length > 0" class="live-bet-list">
            <div class="lbl-header">
              <span class="lbl-title">{{ $t('games.crash.live_bets') }}</span>
              <span class="lbl-count">{{ liveBets.length }}</span>
            </div>
            <div class="lbl-scroll">
              <div
                v-for="bet in liveBets"
                :key="bet.bet_id"
                class="lbl-row"
                :class="bet.status"
              >
                <span class="lbl-player">{{ anonymizeUser(bet.user_id) }}</span>
                <span class="lbl-amount">${{ bet.bet_amount.toFixed(2) }}</span>
                <span class="lbl-status">
                  <template v-if="bet.status === 'cashed_out'">
                    {{ bet.cashout_multiplier?.toFixed(2) }}×
                  </template>
                  <template v-else-if="bet.status === 'lost'">
                    <AppIcon name="bust" :size="14" />
                  </template>
                  <template v-else>
                    <span v-if="bet.auto_cashout" class="lbl-auto">{{ bet.auto_cashout.toFixed(2) }}×</span>
                    <span v-else class="lbl-waiting">…</span>
                  </template>
                </span>
              </div>
            </div>
          </div>

          <!-- Balance row -->
          <div class="balance-row">
            <span class="bal-label">Balance</span>
            <span class="bal-value green">{{ formattedBalance }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import apiClient from '@/api/client'
import { subscribeToPublicChannel, unsubscribe } from '@/composables/useEcho'
import { useWalletStore } from '@/stores/wallet'
import { useToastStore } from '@/stores/toast'
import AppIcon from '@/components/AppIcon.vue'

// ── Constants ────────────────────────────────────────────────
/** Multiplier growth rate per ms — must match CrashGameLoop::DEFAULT_GROWTH_RATE */
const GROWTH_RATE = 0.00006

// ── Types ────────────────────────────────────────────────────
type Phase = 'idle' | 'betting' | 'running' | 'crashed'

interface MyBet {
  id: number
  bet_amount: number
  auto_cashout: number | null
  status: 'pending' | 'cashed_out' | 'lost'
  cashout_multiplier: number | null
  win_amount: number
}

interface LiveBet {
  bet_id: number
  user_id: number
  bet_amount: number
  auto_cashout: number | null
  status: 'pending' | 'cashed_out' | 'lost'
  cashout_multiplier: number | null
  win_amount: number
}

interface HistoryEntry {
  id: number
  crash_point: number
  ended_at: string | null
  server_seed_hash?: string
  server_seed?: string
  outcome_hash?: string
}

// ── Stores & router ──────────────────────────────────────────
const { t } = useI18n()
const router = useRouter()
const walletStore = useWalletStore()
const toastStore = useToastStore()

// ── Game state ───────────────────────────────────────────────
const phase = ref<Phase>('idle')
const roundId = ref<number | null>(null)
const crashPoint = ref<number | null>(null)

/** Elapsed ms from last server tick */
const serverElapsedMs = ref(0)
/** performance.now() timestamp when last tick was received */
const tickLocalTime = ref(0)
/** Smoothly interpolated current multiplier, updated each rAF frame */
const currentMultiplier = ref(1.0)

/** Betting phase ISO-8601 end timestamp */
const bettingEndsAt = ref<string | null>(null)
const bettingCountdown = ref(0)

const hasBetThisRound = ref(false)
const myBet = ref<MyBet | null>(null)
const myBetAmount = ref(0)
const loading = ref(false)
const recentHistory = ref<HistoryEntry[]>([])
const liveBets = ref<LiveBet[]>([])

/** History entry for the currently crashed round (shown in crash result overlay) */
const currentCrashedEntry = ref<HistoryEntry | null>(null)
/** The history entry currently shown in the detail modal */
const selectedHistoryEntry = ref<HistoryEntry | null>(null)

// ── Betting form ─────────────────────────────────────────────
const betAmount = ref(1.0)
const autoCashoutInput = ref<number | null>(null)

// ── Canvas refs ──────────────────────────────────────────────
const chartCanvas = ref<HTMLCanvasElement | null>(null)
const canvasWrap = ref<HTMLElement | null>(null)

// ── WebSocket channel name ───────────────────────────────────
const CRASH_CHANNEL = 'crash'

// ── Animation ────────────────────────────────────────────────
let rafId = 0
let countdownTimer: ReturnType<typeof setInterval> | null = null
let resizeObserver: ResizeObserver | null = null

// ── Computed ─────────────────────────────────────────────────
const canBet = computed(
  () => phase.value === 'betting' && !hasBetThisRound.value && !loading.value,
)

const formattedBalance = computed(() => {
  const bal = walletStore.balance?.balance
  if (bal == null) return '—'
  return `$${bal.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
})

const phaseLabel = computed(() => {
  switch (phase.value) {
    case 'idle':
      return 'Waiting...'
    case 'betting':
      return `${t('games.crash.betting_open')} — ${bettingCountdown.value}s`
    case 'running':
      return `${currentMultiplier.value.toFixed(2)}× — ${t('games.crash.in_progress')}`
    case 'crashed':
      return `${t('games.crash.crashed')} @ ${crashPoint.value?.toFixed(2)}×`
    default:
      return '—'
  }
})

const multColorClass = computed(() => {
  const m = currentMultiplier.value
  if (m >= 10) return 'mult-purple'
  if (m >= 5) return 'mult-gold'
  if (m >= 2) return 'mult-green'
  return 'mult-white'
})

const currentPayoutStr = computed(() => {
  if (!myBet.value) return '0.00'
  return (myBetAmount.value * currentMultiplier.value).toFixed(2)
})

const profitStr = computed(() => {
  if (!myBet.value) return '0.00'
  return Math.max(0, myBetAmount.value * currentMultiplier.value - myBetAmount.value).toFixed(2)
})

// ── Round detail modal ────────────────────────────────────────
function openRoundDetail(entry: HistoryEntry) {
  selectedHistoryEntry.value = entry
}

function closeRoundDetail() {
  selectedHistoryEntry.value = null
}

function formatDateTime(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
  } catch {
    return iso
  }
}

// ── Pure helpers ─────────────────────────────────────────────
/** Same formula as backend CrashGameLoop::calculateMultiplier */
function calcMultiplier(elapsedMs: number): number {
  return Math.floor(100 * Math.exp(GROWTH_RATE * elapsedMs)) / 100
}

function halveBet() {
  betAmount.value = Math.max(0.01, +(betAmount.value / 2).toFixed(2))
}
function doubleBet() {
  betAmount.value = +(betAmount.value * 2).toFixed(2)
}
function maxBet() {
  const bal = walletStore.balance?.balance
  if (bal != null && bal > 0) betAmount.value = +bal.toFixed(2)
}

function historyPillClass(cp: number): string {
  if (cp < 1.5) return 'pill-red'
  if (cp < 2.0) return 'pill-orange'
  if (cp < 5.0) return 'pill-yellow'
  return 'pill-green'
}

/** Anonymize user ID for display (e.g., 4219 → "Player #4219") */
function anonymizeUser(userId: number): string {
  return `#${String(userId).slice(-4).padStart(4, '0')}`
}

function clearCountdown() {
  if (countdownTimer !== null) {
    clearInterval(countdownTimer)
    countdownTimer = null
  }
}

function updateCountdown() {
  if (bettingEndsAt.value === null) {
    bettingCountdown.value = 0
    return
  }
  const remaining = Math.max(
    0,
    Math.ceil((new Date(bettingEndsAt.value).getTime() - Date.now()) / 1000),
  )
  bettingCountdown.value = remaining
}

// ── WebSocket event handlers ──────────────────────────────────
interface PhaseChangedPayload {
  round_id: number
  phase: string
  server_seed_hash: string
  betting_ends_at?: string
  crash_point?: number
  server_seed?: string
  outcome_hash?: string
}

function handlePhaseChanged(data: PhaseChangedPayload) {
  roundId.value = data.round_id

  if (data.phase === 'betting') {
    phase.value = 'betting'
    crashPoint.value = null
    bettingEndsAt.value = data.betting_ends_at ?? null
    serverElapsedMs.value = 0
    tickLocalTime.value = 0
    currentMultiplier.value = 1.0
    myBet.value = null
    hasBetThisRound.value = false
    myBetAmount.value = 0
    liveBets.value = []
    currentCrashedEntry.value = null
    clearCountdown()
    updateCountdown()
    countdownTimer = setInterval(updateCountdown, 200)
  } else if (data.phase === 'running') {
    phase.value = 'running'
    clearCountdown()
    bettingCountdown.value = 0
    serverElapsedMs.value = 0
    tickLocalTime.value = performance.now()
    currentMultiplier.value = 1.0
  } else if (data.phase === 'crashed') {
    phase.value = 'crashed'
    if (data.crash_point !== undefined) {
      crashPoint.value = data.crash_point
    }
    clearCountdown()

    // Append to history strip (with PF data from WS event)
    if (data.crash_point !== undefined) {
      const entry: HistoryEntry = {
        id: data.round_id,
        crash_point: data.crash_point,
        ended_at: new Date().toISOString(),
        server_seed_hash: data.server_seed_hash,
        server_seed: data.server_seed,
        outcome_hash: data.outcome_hash,
      }
      recentHistory.value.unshift(entry)
      if (recentHistory.value.length > 30) {
        recentHistory.value.pop()
      }
      currentCrashedEntry.value = entry
    }

    // Mark pending bet as lost if not already cashed out
    if (myBet.value && myBet.value.status === 'pending') {
      myBet.value = { ...myBet.value, status: 'lost' }
    }
  }
}

interface TickPayload {
  round_id: number
  multiplier: number
  elapsed_ms: number
}

function handleMultiplierTick(data: TickPayload) {
  if (data.round_id !== roundId.value) return
  serverElapsedMs.value = data.elapsed_ms
  tickLocalTime.value = performance.now()
}

interface BetPlacedPayload {
  round_id: number
  bet_id: number
  user_id: number
  bet_amount: number
  auto_cashout: number | null
  currency: string
}

function handleBetPlaced(data: BetPlacedPayload) {
  if (data.round_id !== roundId.value) return
  // Avoid duplicates (may arrive while we already loaded via state endpoint)
  if (liveBets.value.some((b) => b.bet_id === data.bet_id)) return
  liveBets.value.unshift({
    bet_id: data.bet_id,
    user_id: data.user_id,
    bet_amount: data.bet_amount,
    auto_cashout: data.auto_cashout,
    status: 'pending',
    cashout_multiplier: null,
    win_amount: 0,
  })
}

interface CashedOutPayload {
  round_id: number
  bet_id: number
  user_id: number
  bet_amount: number
  cashout_multiplier: number
  win_amount: number
  currency: string
}

function handlePlayerCashedOut(data: CashedOutPayload) {
  if (data.round_id !== roundId.value) return
  const bet = liveBets.value.find((b) => b.bet_id === data.bet_id)
  if (bet) {
    bet.status = 'cashed_out'
    bet.cashout_multiplier = data.cashout_multiplier
    bet.win_amount = data.win_amount
  }
}

// ── WebSocket connection ─────────────────────────────────────
function connectWebSocket() {
  subscribeToPublicChannel<PhaseChangedPayload>(CRASH_CHANNEL, '.round.phase_changed', handlePhaseChanged)
  subscribeToPublicChannel<TickPayload>(CRASH_CHANNEL, '.multiplier.tick', handleMultiplierTick)
  subscribeToPublicChannel<BetPlacedPayload>(CRASH_CHANNEL, '.player.bet_placed', handleBetPlaced)
  subscribeToPublicChannel<CashedOutPayload>(CRASH_CHANNEL, '.player.cashed_out', handlePlayerCashedOut)
}

// ── Load initial state (for reconnection recovery) ───────────
interface RoundState {
  id: number
  status: string
  crash_point?: number
  betting_ends_at?: string
}

interface StateResponse {
  status: 'idle' | 'active'
  round?: RoundState
  elapsed_ms?: number
  my_bet?: MyBet | null
  bets?: LiveBet[]
  last_round?: { id: number; crash_point: number } | null
}

async function loadState() {
  try {
    const res = await apiClient.get<StateResponse>('/api/games/crash/state')
    const data = res.data

    if (data.status === 'idle' || !data.round) {
      phase.value = 'idle'
      return
    }

    const round = data.round
    roundId.value = round.id

    if (round.status === 'betting') {
      phase.value = 'betting'
      bettingEndsAt.value = round.betting_ends_at ?? null
      updateCountdown()
      countdownTimer = setInterval(updateCountdown, 200)
    } else if (round.status === 'running') {
      phase.value = 'running'
      const resumeElapsed = data.elapsed_ms ?? 0
      serverElapsedMs.value = resumeElapsed
      tickLocalTime.value = performance.now()
    } else if (round.status === 'crashed') {
      phase.value = 'crashed'
      crashPoint.value = round.crash_point ?? null
    }

    if (data.my_bet) {
      myBet.value = data.my_bet
      myBetAmount.value = data.my_bet.bet_amount
      hasBetThisRound.value = true
    }

    if (data.bets) {
      liveBets.value = data.bets
    }
  } catch {
    // Game loop not running — stay idle
  }
}

interface HistoryRound {
  id: number
  crash_point: number
  ended_at: string | null
  server_seed_hash?: string
  server_seed?: string
  outcome_hash?: string
}

interface HistoryResponse {
  rounds: HistoryRound[]
}

async function loadHistory() {
  try {
    const res = await apiClient.get<HistoryResponse>('/api/games/crash/history?limit=30')
    recentHistory.value = res.data.rounds.map((r) => ({
      id: r.id,
      crash_point: r.crash_point,
      ended_at: r.ended_at,
      server_seed_hash: r.server_seed_hash,
      server_seed: r.server_seed,
      outcome_hash: r.outcome_hash,
    }))
  } catch {
    // Silent — history is non-critical
  }
}

// ── API actions ──────────────────────────────────────────────
interface BetResponse {
  bet_id: number
  round_id: number
  bet_amount: number
  auto_cashout: number | null
}

async function placeBet() {
  if (!canBet.value) return
  if (!betAmount.value || betAmount.value <= 0) {
    toastStore.error(t('games.invalid_bet_amount'))
    return
  }
  const bal = walletStore.balance?.balance ?? 0
  if (betAmount.value > bal) {
    toastStore.error(t('games.insufficient_balance'))
    return
  }

  loading.value = true
  try {
    const payload: { amount: string; auto_cashout?: number } = {
      amount: betAmount.value.toFixed(2),
    }
    if (autoCashoutInput.value !== null && autoCashoutInput.value >= 1.01) {
      payload.auto_cashout = autoCashoutInput.value
    }

    const res = await apiClient.post<BetResponse>('/api/games/crash/bet', payload)
    hasBetThisRound.value = true
    myBetAmount.value = res.data.bet_amount
    myBet.value = {
      id: res.data.bet_id,
      bet_amount: res.data.bet_amount,
      auto_cashout: res.data.auto_cashout,
      status: 'pending',
      cashout_multiplier: null,
      win_amount: 0,
    }
    walletStore.applyGameResult(res.data.bet_amount, 0)
    toastStore.success(`Bet $${betAmount.value.toFixed(2)} placed!`)
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    toastStore.error(e?.response?.data?.message ?? 'Failed to place bet')
  } finally {
    loading.value = false
  }
}

interface CashoutResponse {
  cashout_multiplier: number
  win_amount: number
  bet_amount: number
  status: string
}

async function cashout() {
  if (loading.value || phase.value !== 'running' || !myBet.value || myBet.value.status !== 'pending')
    return

  loading.value = true
  try {
    const res = await apiClient.post<CashoutResponse>('/api/games/crash/cashout', {})
    myBet.value = {
      ...myBet.value,
      status: 'cashed_out',
      cashout_multiplier: res.data.cashout_multiplier,
      win_amount: res.data.win_amount,
    }
    walletStore.applyGameResult(0, res.data.win_amount)
    toastStore.success(
      `Cashed out $${res.data.win_amount.toFixed(2)} @ ${res.data.cashout_multiplier.toFixed(2)}×!`,
    )
  } catch (err: unknown) {
    const e = err as { response?: { data?: { message?: string } } }
    toastStore.error(e?.response?.data?.message ?? 'Cashout failed')
  } finally {
    loading.value = false
  }
}

// ── Graph rendering ──────────────────────────────────────────
function syncCanvasSize() {
  const canvas = chartCanvas.value
  if (!canvas) return
  const dpr = window.devicePixelRatio || 1
  const cssW = canvas.clientWidth
  const cssH = canvas.clientHeight
  const targetW = Math.round(cssW * dpr)
  const targetH = Math.round(cssH * dpr)
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width = targetW
    canvas.height = targetH
  }
}

function chooseMultStep(maxMult: number): number {
  const range = maxMult - 1.0
  if (range <= 1.5) return 0.5
  if (range <= 4) return 1
  if (range <= 9) return 2
  if (range <= 24) return 5
  if (range <= 49) return 10
  if (range <= 99) return 20
  return 100
}

function chooseTimeStep(maxT: number): number {
  if (maxT <= 15) return 5
  if (maxT <= 30) return 10
  if (maxT <= 60) return 15
  if (maxT <= 120) return 30
  return 60
}

function drawGraph() {
  const canvas = chartCanvas.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  if (!ctx) return

  const dpr = window.devicePixelRatio || 1
  const CW = canvas.width / dpr
  const CH = canvas.height / dpr

  ctx.save()
  ctx.scale(dpr, dpr)
  ctx.clearRect(0, 0, CW, CH)

  const phaseCur = phase.value
  const isRunning = phaseCur === 'running'
  const isCrashed = phaseCur === 'crashed'
  const isActive = isRunning || isCrashed

  // Compute current elapsed ms and multiplier
  let elapsedMs = 0
  let mult = 1.0

  if (isRunning) {
    const localDelta = performance.now() - tickLocalTime.value
    elapsedMs = Math.max(0, serverElapsedMs.value + localDelta)
    mult = Math.max(1.0, calcMultiplier(elapsedMs))
    currentMultiplier.value = mult
  } else if (isCrashed && crashPoint.value !== null) {
    mult = crashPoint.value
    // Approximate elapsed time from crash point (inverse formula)
    elapsedMs = (Math.log(mult) / GROWTH_RATE) * 1.01
  }

  // Axis ranges
  const maxMult =
    isActive ? Math.max(2.0, mult * 1.35) : 3.0
  const maxTimeSec =
    isActive ? Math.max(20, (elapsedMs / 1000) * 1.35) : 20

  // Graph padding
  const padL = 50
  const padR = 16
  const padT = 20
  const padB = 32
  const gW = CW - padL - padR
  const gH = CH - padT - padB

  // Coordinate helpers
  const mToY = (m: number): number => {
    const clamped = Math.max(1.0, Math.min(maxMult, m))
    return padT + gH - ((clamped - 1.0) / (maxMult - 1.0)) * gH
  }
  const tToX = (tSec: number): number => padL + (Math.min(tSec, maxTimeSec) / maxTimeSec) * gW

  // ── Background fill for graph area ──────────────────────────
  ctx.fillStyle = 'rgba(0,0,0,0.2)'
  ctx.beginPath()
  ctx.rect(padL, padT, gW, gH)
  ctx.fill()

  // ── Grid lines ───────────────────────────────────────────────
  ctx.lineWidth = 1
  const multStep = chooseMultStep(maxMult)
  ctx.font = '10px JetBrains Mono, monospace'
  ctx.textAlign = 'right'
  ctx.textBaseline = 'middle'

  for (let m = 1.0; m <= maxMult + 0.001; m = +(m + multStep).toFixed(8)) {
    const y = mToY(m)
    if (y < padT - 2 || y > padT + gH + 2) continue

    ctx.strokeStyle = 'rgba(255,255,255,0.06)'
    ctx.beginPath()
    ctx.moveTo(padL, y)
    ctx.lineTo(padL + gW, y)
    ctx.stroke()

    // Y-axis label
    ctx.fillStyle = 'rgba(178,186,211,0.55)'
    const label = m >= 10 ? `${m.toFixed(0)}×` : `${m.toFixed(1)}×`
    ctx.fillText(label, padL - 5, y)
  }

  const timeStep = chooseTimeStep(maxTimeSec)
  ctx.textAlign = 'center'
  ctx.textBaseline = 'top'

  for (let t = 0; t <= maxTimeSec + 0.001; t += timeStep) {
    const x = tToX(t)
    if (x < padL - 2 || x > padL + gW + 2) continue

    ctx.strokeStyle = 'rgba(255,255,255,0.06)'
    ctx.beginPath()
    ctx.moveTo(x, padT)
    ctx.lineTo(x, padT + gH)
    ctx.stroke()

    if (t > 0) {
      ctx.fillStyle = 'rgba(178,186,211,0.45)'
      ctx.fillText(`${t}s`, x, padT + gH + 5)
    }
  }

  // ── Axes ──────────────────────────────────────────────────────
  ctx.strokeStyle = 'rgba(255,255,255,0.15)'
  ctx.lineWidth = 1
  ctx.beginPath()
  ctx.moveTo(padL, padT)
  ctx.lineTo(padL, padT + gH)
  ctx.lineTo(padL + gW, padT + gH)
  ctx.stroke()

  // ── Multiplier curve ──────────────────────────────────────────
  if (isActive && elapsedMs > 0) {
    const steps = Math.max(60, Math.min(400, Math.floor(elapsedMs / 40)))
    const lineColor = isCrashed ? '#ed4245' : '#00e701'
    const glowColor = isCrashed ? 'rgba(237,66,69,0.25)' : 'rgba(0,231,1,0.25)'

    // Fill area under curve
    ctx.beginPath()
    ctx.moveTo(tToX(0), mToY(1.0))
    for (let i = 1; i <= steps; i++) {
      const tMs = (i / steps) * elapsedMs
      ctx.lineTo(tToX(tMs / 1000), mToY(calcMultiplier(tMs)))
    }
    ctx.lineTo(tToX(elapsedMs / 1000), mToY(1.0))
    ctx.closePath()
    ctx.fillStyle = isCrashed ? 'rgba(237,66,69,0.06)' : 'rgba(0,231,1,0.06)'
    ctx.fill()

    // Glow pass (thick, transparent)
    ctx.beginPath()
    ctx.moveTo(tToX(0), mToY(1.0))
    for (let i = 1; i <= steps; i++) {
      const tMs = (i / steps) * elapsedMs
      ctx.lineTo(tToX(tMs / 1000), mToY(calcMultiplier(tMs)))
    }
    ctx.strokeStyle = glowColor
    ctx.lineWidth = 8
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()

    // Main line
    ctx.beginPath()
    ctx.moveTo(tToX(0), mToY(1.0))
    for (let i = 1; i <= steps; i++) {
      const tMs = (i / steps) * elapsedMs
      ctx.lineTo(tToX(tMs / 1000), mToY(calcMultiplier(tMs)))
    }
    ctx.strokeStyle = lineColor
    ctx.lineWidth = 2.5
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.stroke()

    // Endpoint dot
    const endX = tToX(elapsedMs / 1000)
    const endY = mToY(Math.min(maxMult, mult))
    ctx.beginPath()
    ctx.arc(endX, endY, 5, 0, Math.PI * 2)
    ctx.fillStyle = lineColor
    ctx.shadowColor = lineColor
    ctx.shadowBlur = 14
    ctx.fill()
    ctx.shadowBlur = 0

    // Crash marker (vertical dashed line)
    if (isCrashed) {
      ctx.setLineDash([4, 4])
      ctx.strokeStyle = 'rgba(237,66,69,0.5)'
      ctx.lineWidth = 1
      ctx.beginPath()
      ctx.moveTo(endX, padT)
      ctx.lineTo(endX, padT + gH)
      ctx.stroke()
      ctx.setLineDash([])
    }
  } else if (!isActive) {
    // Show a faint "ready" line at 1.0×
    ctx.strokeStyle = 'rgba(0,231,1,0.2)'
    ctx.lineWidth = 1.5
    ctx.setLineDash([6, 6])
    ctx.beginPath()
    ctx.moveTo(tToX(0), mToY(1.0))
    ctx.lineTo(tToX(maxTimeSec), mToY(1.0))
    ctx.stroke()
    ctx.setLineDash([])
  }

  ctx.restore()
}

// ── rAF loop ─────────────────────────────────────────────────
function animateFrame() {
  syncCanvasSize()
  drawGraph()
  rafId = requestAnimationFrame(animateFrame)
}

// ── Lifecycle ────────────────────────────────────────────────
onMounted(async () => {
  if (!walletStore.balance) {
    walletStore.fetchBalance()
  }

  // ResizeObserver for responsive canvas
  if (canvasWrap.value) {
    resizeObserver = new ResizeObserver(() => syncCanvasSize())
    resizeObserver.observe(canvasWrap.value)
  }

  // Start rAF loop
  rafId = requestAnimationFrame(animateFrame)

  // Connect WebSocket
  connectWebSocket()

  // Load current state + history
  await Promise.all([loadState(), loadHistory()])
})

onUnmounted(() => {
  cancelAnimationFrame(rafId)
  clearCountdown()
  resizeObserver?.disconnect()
  unsubscribe(CRASH_CHANNEL)
})
</script>

<style scoped>
/* ── Layout ── */
.crash-game {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  animation: fadeIn 0.25s ease both;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

/* ── Topbar ── */
.game-topbar {
  height: 48px;
  background: var(--bg-secondary, #1a2c38);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  align-items: center;
  padding: 0 16px;
  gap: 12px;
  flex-shrink: 0;
}

.btn-back {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 600;
  padding: 6px 12px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
}
.btn-back:hover {
  color: var(--color-on-dark, #fff);
  background: var(--bg-hover, #2a4455);
}

.game-title-bar {
  font-size: 14px;
  font-weight: 700;
  display: flex;
  align-items: center;
  gap: 8px;
}

.game-tag {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  padding: 2px 8px;
  border-radius: 9999px;
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.2);
}

.game-topbar-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
}

.icon-btn {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: var(--bg-tertiary, #213743);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  cursor: pointer;
  color: var(--text-secondary, #b1bad3);
  transition: all 150ms ease;
  border: none;
}
.icon-btn:hover {
  background: rgba(0, 231, 1, 0.1);
  color: var(--accent-primary, #00e701);
}

/* ── Game body ── */
.game-body {
  display: flex;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

/* ── Canvas area ── */
.game-canvas-area {
  flex: 1;
  background: var(--bg-game, #0d1b2a);
  position: relative;
  overflow: hidden;
  min-width: 0;
}

.canvas-bg-grid {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(0, 231, 1, 0.018) 1px, transparent 1px),
    linear-gradient(90deg, rgba(0, 231, 1, 0.018) 1px, transparent 1px);
  background-size: 40px 40px;
  pointer-events: none;
}

.crash-chart {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: block;
}

/* ── Overlays ── */
.phase-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  z-index: 5;
  pointer-events: none;
}

.betting-overlay {
  background: rgba(13, 27, 42, 0.55);
  backdrop-filter: blur(2px);
}

.idle-overlay {
  background: rgba(13, 27, 42, 0.4);
}

.overlay-label {
  font-size: 11px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--text-tertiary, #7f8fa4);
}

.overlay-countdown {
  font-family: 'JetBrains Mono', monospace;
  font-size: 56px;
  font-weight: 900;
  color: var(--accent-primary, #00e701);
  text-shadow: 0 0 40px rgba(0, 231, 1, 0.6);
  line-height: 1;
}

.overlay-hint {
  font-size: 14px;
  color: var(--text-secondary, #b1bad3);
  font-weight: 500;
}

.overlay-icon {
  font-size: 48px;
  margin-bottom: 4px;
  opacity: 0.5;
}

/* ── Live multiplier display ── */
.live-mult-display {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
  z-index: 5;
  pointer-events: none;
  text-align: center;
}

.live-mult-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 64px;
  font-weight: 900;
  line-height: 1;
  transition: color 0.3s ease;
}

.mult-white { color: var(--color-on-dark, #fff); }
.mult-green { color: var(--accent-primary, #00e701); text-shadow: 0 0 40px rgba(0, 231, 1, 0.5); }
.mult-gold  { color: var(--accent-gold, #ffd700);   text-shadow: 0 0 40px rgba(255, 215, 0, 0.5); }
.mult-purple { color: var(--accent-purple, #9b59b6); text-shadow: 0 0 40px rgba(155, 89, 182, 0.6); }

.mult-x {
  font-size: 0.65em;
  opacity: 0.8;
}

/* ── Crash result display ── */
.crash-display {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -60%);
  z-index: 5;
  pointer-events: none;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.crash-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 64px;
  font-weight: 900;
  line-height: 1;
  color: var(--color-danger, #ed4245);
  text-shadow: 0 0 40px rgba(237, 66, 69, 0.5);
}

.crash-label {
  font-size: 13px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: var(--color-danger, #ed4245);
  opacity: 0.85;
}

.player-result {
  margin-top: 8px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 16px;
  font-weight: 700;
  padding: 6px 16px;
  border-radius: 8px;
}
.player-result.win {
  color: var(--color-success, #00e701);
  background: rgba(0, 231, 1, 0.1);
  border: 1px solid rgba(0, 231, 1, 0.2);
}
.player-result.loss {
  color: var(--color-danger, #ed4245);
  background: rgba(237, 66, 69, 0.1);
  border: 1px solid rgba(237, 66, 69, 0.2);
}

/* ── Overlay transitions ── */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.mult-pop-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.mult-pop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.mult-pop-enter-from {
  opacity: 0;
  transform: translate(-50%, -55%) scale(0.85);
}
.mult-pop-leave-to {
  opacity: 0;
  transform: translate(-50%, -65%) scale(1.05);
}

/* ── History strip ── */
.history-strip {
  position: absolute;
  top: 10px;
  left: 56px;
  right: 16px;
  display: flex;
  gap: 5px;
  overflow: hidden;
  z-index: 6;
  flex-wrap: nowrap;
}

.history-pill {
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 99px;
  white-space: nowrap;
  flex-shrink: 0;
  opacity: 0.85;
  cursor: pointer;
  transition: opacity 120ms ease, transform 120ms ease;
}
.history-pill:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.pill-red    { background: rgba(237, 66, 69, 0.2);  color: var(--color-danger, #ed4245); border: 1px solid rgba(237,66,69,0.3); }
.pill-orange { background: rgba(255, 167, 38, 0.2); color: var(--color-warning, #ffa726); border: 1px solid rgba(255,167,38,0.3); }
.pill-yellow { background: rgba(255, 215, 0, 0.15); color: var(--accent-gold, #ffd700); border: 1px solid rgba(255,215,0,0.25); }
.pill-green  { background: rgba(0, 231, 1, 0.15);   color: var(--accent-primary, #00e701); border: 1px solid rgba(0,231,1,0.25); }

/* ── Legend ── */
.graph-legend {
  position: absolute;
  bottom: 38px;
  right: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
  z-index: 6;
  pointer-events: none;
}

.legend-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  color: var(--text-tertiary, #7f8fa4);
}

.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}
.legend-dot.green { background: var(--accent-primary, #00e701); box-shadow: 0 0 5px rgba(0, 231, 1, 0.6); }
.legend-dot.red   { background: var(--color-danger, #ed4245); box-shadow: 0 0 5px rgba(237, 66, 69, 0.6); }

.legend-item.elapsed {
  font-family: 'JetBrains Mono', monospace;
  color: rgba(178, 186, 211, 0.6);
}

/* ── Sidebar ── */
.game-sidebar {
  width: 280px;
  background: var(--bg-secondary, #1a2c38);
  border-left: 1px solid rgba(255, 255, 255, 0.06);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex-shrink: 0;
}

.game-tabs {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
}

.game-tab {
  flex: 1;
  padding: 13px 4px;
  font-size: 11px;
  font-weight: 700;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: all 150ms ease;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: none;
  border-top: none;
  border-left: none;
  border-right: none;
  font-family: inherit;
}
.game-tab.active {
  color: var(--accent-primary, #00e701);
  border-bottom-color: var(--accent-primary, #00e701);
}
.game-tab:hover:not(.active) {
  color: var(--text-secondary, #b1bad3);
}

.game-tab-content {
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 0;
}
.game-tab-content::-webkit-scrollbar { width: 3px; }

/* ── Phase chip ── */
.phase-chip {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 700;
  background: var(--bg-tertiary, #213743);
  border: 1px solid rgba(255, 255, 255, 0.06);
  margin-bottom: 0;
}

.phase-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.phase-chip.idle .phase-dot    { background: var(--text-tertiary, #7f8fa4); }
.phase-chip.betting .phase-dot { background: var(--accent-secondary, #1475e1); box-shadow: 0 0 8px rgba(20,117,225,0.6); animation: blink 1s ease infinite; }
.phase-chip.running .phase-dot { background: var(--accent-primary, #00e701); box-shadow: 0 0 10px rgba(0,231,1,0.7); }
.phase-chip.crashed .phase-dot { background: var(--color-danger, #ed4245); }

.phase-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.3; }
}

/* ── Controls ── */
.ctrl-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 6px;
  display: block;
}

.bet-input-wrap {
  position: relative;
  display: flex;
  align-items: center;
  margin-bottom: 6px;
}

.bet-currency {
  position: absolute;
  left: 10px;
  font-size: 13px;
  color: var(--text-tertiary, #7f8fa4);
  pointer-events: none;
}

.bet-input {
  background: var(--bg-primary, #0f1923);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 10px 10px 10px 24px;
  color: var(--text-primary, #fff);
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  transition: border-color 150ms ease;
}
.bet-input:focus { outline: none; border-color: rgba(20, 117, 225, 0.5); }
.bet-input:disabled { opacity: 0.45; }
.bet-input::placeholder { color: var(--text-tertiary, #7f8fa4); font-size: 12px; }

.bet-shortcuts {
  display: flex;
  gap: 4px;
  margin-bottom: 14px;
}

.bet-short {
  flex: 1;
  padding: 6px 0;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  font-size: 11px;
  font-weight: 700;
  border-radius: 4px;
  cursor: pointer;
  border: none;
  transition: all 150ms ease;
  text-align: center;
  font-family: inherit;
}
.bet-short:hover:not(:disabled) { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }
.bet-short:disabled { opacity: 0.35; cursor: not-allowed; }

/* ── Buttons ── */
.btn-action {
  width: 100%;
  padding: 14px;
  background: var(--accent-primary, #00e701);
  color: var(--color-on-accent, #000);
  font-size: 15px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.03em;
  margin-bottom: 8px;
  font-family: inherit;
}
.btn-action:hover:not(:disabled) {
  background: var(--accent-primary-hover, #00cc01);
  box-shadow: 0 0 24px rgba(0, 231, 1, 0.35);
  transform: translateY(-1px);
}
.btn-action:disabled { opacity: 0.4; cursor: not-allowed; }
.btn-action:active:not(:disabled) { transform: translateY(0); }

.btn-action.bet-confirmed {
  background: rgba(0, 231, 1, 0.15);
  color: var(--accent-primary, #00e701);
  border: 1px solid rgba(0, 231, 1, 0.3);
  font-size: 12px;
}

.btn-cashout {
  width: 100%;
  padding: 14px;
  background: var(--color-warning, #ffa726);
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 800;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 150ms ease;
  letter-spacing: 0.02em;
  margin-bottom: 8px;
  font-family: inherit;
  animation: cashoutPulse 1.2s ease infinite;
}
.btn-cashout:hover:not(:disabled) {
  background: var(--color-warning, #e09000);
  box-shadow: 0 0 24px rgba(255, 167, 38, 0.5);
  transform: translateY(-1px);
  animation: none;
}
.btn-cashout:disabled { opacity: 0.4; cursor: not-allowed; animation: none; }
.btn-cashout:active:not(:disabled) { transform: translateY(0); }

@keyframes cashoutPulse {
  0%, 100% { box-shadow: 0 0 12px rgba(255, 167, 38, 0.3); }
  50% { box-shadow: 0 0 28px rgba(255, 167, 38, 0.6); }
}

/* ── State indicators ── */
.next-round-notice {
  width: 100%;
  padding: 12px;
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  font-size: 12px;
  color: var(--text-tertiary, #7f8fa4);
  text-align: center;
  margin-bottom: 8px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.cashed-out-badge {
  width: 100%;
  padding: 12px;
  background: rgba(0, 231, 1, 0.1);
  border: 1px solid rgba(0, 231, 1, 0.25);
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  color: var(--accent-primary, #00e701);
  text-align: center;
  margin-bottom: 8px;
}

.payout-preview {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  margin-bottom: 8px;
}

.payout-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.payout-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 800;
  color: var(--color-warning, #ffa726);
}

/* ── Stat pills ── */
.stat-pills {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-bottom: 10px;
}

.stat-pill {
  background: var(--bg-tertiary, #213743);
  border-radius: 8px;
  padding: 8px 10px;
  border: 1px solid rgba(255, 255, 255, 0.04);
}

.sp-label {
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
  margin-bottom: 3px;
}

.sp-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}
.sp-value.gold { color: var(--accent-gold, #ffd700); }
.sp-value.green { color: var(--accent-primary, #00e701); }

/* ── Auto-cashout badge ── */
.auto-cashout-badge {
  padding: 6px 10px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.25);
  border-radius: 6px;
  font-size: 11px;
  font-weight: 700;
  color: var(--accent-secondary, #1475e1);
  text-align: center;
  margin-bottom: 8px;
}

/* ── Live bet list ── */
.live-bet-list {
  margin-top: auto;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  padding-top: 10px;
  margin-bottom: 10px;
  flex-shrink: 0;
}

.lbl-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
}

.lbl-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.lbl-count {
  font-size: 10px;
  font-weight: 700;
  background: var(--bg-tertiary, #213743);
  color: var(--text-secondary, #b1bad3);
  padding: 1px 6px;
  border-radius: 99px;
}

.lbl-scroll {
  max-height: 140px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 3px;
}
.lbl-scroll::-webkit-scrollbar { width: 2px; }

.lbl-row {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px;
  border-radius: 5px;
  background: var(--bg-tertiary, #213743);
  font-size: 11px;
  transition: background 0.2s ease;
}
.lbl-row.cashed_out { background: rgba(0, 231, 1, 0.07); }
.lbl-row.lost       { background: rgba(237, 66, 69, 0.07); }

.lbl-player {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
  flex-shrink: 0;
  width: 42px;
}

.lbl-amount {
  font-family: 'JetBrains Mono', monospace;
  color: var(--text-secondary, #b1bad3);
  flex: 1;
}

.lbl-status {
  font-family: 'JetBrains Mono', monospace;
  font-weight: 700;
  font-size: 11px;
  flex-shrink: 0;
  text-align: right;
  min-width: 48px;
}

.lbl-row.cashed_out .lbl-status { color: var(--accent-primary, #00e701); }
.lbl-row.lost .lbl-status       { color: var(--color-danger, #ed4245); }
.lbl-row.pending .lbl-status    { color: var(--text-tertiary, #7f8fa4); }

.lbl-auto {
  color: var(--accent-secondary, #1475e1);
  font-size: 10px;
}

.lbl-waiting {
  color: var(--text-tertiary, #7f8fa4);
  letter-spacing: 0.1em;
}

/* ── Balance row ── */
.balance-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  margin-top: auto;
}

.bal-label {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

.bal-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 13px;
  font-weight: 700;
  color: var(--text-secondary, #b1bad3);
}
.bal-value.green { color: var(--color-success, #00e701); }

/* ── Provably fair verify link (crash result) ── */
.pf-verify-link {
  margin-top: 10px;
  padding: 5px 14px;
  background: rgba(20, 117, 225, 0.1);
  border: 1px solid rgba(20, 117, 225, 0.25);
  border-radius: 99px;
  font-size: 11px;
  font-weight: 700;
  color: var(--accent-secondary, #1475e1);
  cursor: pointer;
  transition: all 150ms ease;
  font-family: inherit;
  pointer-events: all;
}
.pf-verify-link:hover {
  background: rgba(20, 117, 225, 0.2);
  border-color: rgba(20, 117, 225, 0.5);
}

/* ── Round detail modal ── */
.round-detail-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(4px);
  z-index: 30;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

.round-detail-modal {
  background: var(--bg-secondary, #1a2c38);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  width: 100%;
  max-width: 440px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.6);
}
.round-detail-modal::-webkit-scrollbar { width: 3px; }

.rdm-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.rdm-title {
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.rdm-close {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: var(--bg-tertiary, #213743);
  border: none;
  color: var(--text-secondary, #b1bad3);
  font-size: 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 150ms ease;
  font-family: inherit;
}
.rdm-close:hover { background: var(--bg-hover, #2a4455); color: var(--color-on-dark, #fff); }

.rdm-body {
  padding: 16px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.rdm-row {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.rdm-label {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--text-tertiary, #7f8fa4);
}

.rdm-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 14px;
  font-weight: 700;
  color: var(--text-primary, #fff);
}

.rdm-value.crash-color {
  color: var(--color-danger, #ed4245);
}

.rdm-hash {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--text-secondary, #b1bad3);
  word-break: break-all;
  line-height: 1.5;
  background: var(--bg-primary, #0f1923);
  padding: 6px 8px;
  border-radius: 6px;
  border: 1px solid rgba(255, 255, 255, 0.06);
}

.rdm-section-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--accent-secondary, #1475e1);
  padding-top: 6px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
}

.rdm-explain {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
  line-height: 1.6;
  background: var(--bg-tertiary, #213743);
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.rdm-explain code {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: var(--accent-primary, #00e701);
  background: rgba(0, 231, 1, 0.06);
  padding: 1px 4px;
  border-radius: 3px;
}

.rdm-explain em {
  color: var(--text-secondary, #b1bad3);
  font-style: normal;
}

/* ── Modal transition ── */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
  opacity: 0;
}
.modal-fade-enter-active .round-detail-modal,
.modal-fade-leave-active .round-detail-modal {
  transition: transform 0.2s ease;
}
.modal-fade-enter-from .round-detail-modal {
  transform: scale(0.94);
}
.modal-fade-leave-to .round-detail-modal {
  transform: scale(0.94);
}

/* ── Responsive ── */
@media (max-width: 900px) {
  .game-sidebar { width: 240px; }
}

@media (max-width: 768px) {
  .game-body { flex-direction: column; }
  .game-sidebar {
    width: 100%;
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    max-height: 320px;
    flex-shrink: 0;
  }
  .overlay-countdown { font-size: 40px; }
  .live-mult-value { font-size: 48px; }
  .crash-value { font-size: 48px; }
}

@media (max-width: 480px) {
  .history-strip { left: 10px; }
  .graph-legend { display: none; }
}
</style>
